import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, Calendar, Trophy, RefreshCw, Loader2, Check, Info, Star, ExternalLink, Trash2 } from "lucide-react";
import { Capacitor } from "@capacitor/core";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const APP_VERSION = "1.0.0";

// App Store / Play Store URLs
const APP_STORE_URL = "https://apps.apple.com/app/apna-premier-league/id123456789"; // Replace with actual
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.apl.app"; // Replace with actual

export default function SettingsPage() {
  const { getAuthHeader, setDbUser, dbUser, user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  
  const [prefs, setPrefs] = useState({
    match_reminders: true,
    daily_reminders: true,
    league_notifications: true
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/users/settings`, { headers: getAuthHeader() });
        if (res.data.notification_prefs) {
          setPrefs(res.data.notification_prefs);
        }
      } catch (err) {
        console.error("Failed to fetch settings:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [getAuthHeader]);

  const handleToggle = async (key) => {
    const newPrefs = { ...prefs, [key]: !prefs[key] };
    setPrefs(newPrefs);
    
    setSaving(true);
    try {
      await axios.put(`${API}/users/settings`, {
        notification_prefs: newPrefs
      }, { headers: getAuthHeader() });
      setSuccess("Settings saved");
      setTimeout(() => setSuccess(""), 2000);
    } catch (err) {
      console.error("Failed to save settings:", err);
      setPrefs(prefs);
    } finally {
      setSaving(false);
    }
  };

  const handleReplayTutorial = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/settings`, {
        has_seen_tutorial: false
      }, { headers: getAuthHeader() });
      
      if (dbUser) {
        setDbUser({ ...dbUser, has_seen_tutorial: false });
      }
      navigate("/");
    } catch (err) {
      console.error("Failed to reset tutorial:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleRateAndReview = async () => {
    const platform = Capacitor.getPlatform();
    
    // Try native in-app review first
    if (Capacitor.isNativePlatform()) {
      try {
        const { InAppReview } = await import("@capacitor-community/in-app-review");
        await InAppReview.requestReview();
        return;
      } catch (err) {
        console.log("Native review failed, falling back to store URL:", err);
      }
    }
    
    // Fallback to store URL
    let storeUrl;
    if (platform === "ios") {
      storeUrl = APP_STORE_URL;
    } else if (platform === "android") {
      storeUrl = PLAY_STORE_URL;
    } else {
      // Web - open Play Store by default
      storeUrl = PLAY_STORE_URL;
    }
    
    window.open(storeUrl, "_blank");
  };

  const Toggle = ({ checked, onChange, disabled }) => (
    <button
      onClick={onChange}
      disabled={disabled}
      className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
        checked ? "bg-emerald-500" : "bg-slate-600"
      } ${disabled ? "opacity-50" : ""}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
          checked ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-emerald-400" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto pb-8" data-testid="settings-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2">Settings</h1>
        <p className="text-slate-400 text-sm">Manage your preferences</p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-4 p-3 bg-emerald-500/20 border border-emerald-500/30 rounded-lg text-emerald-400 text-sm flex items-center gap-2">
          <Check size={16} />
          {success}
        </div>
      )}

      {/* Notification Preferences */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="text-emerald-400" size={20} />
          <h2 className="text-lg font-semibold text-white">Notifications</h2>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="text-slate-400" size={18} />
              <div>
                <p className="text-white text-sm font-medium">Match Reminders</p>
                <p className="text-slate-500 text-xs">Get notified before matches start</p>
              </div>
            </div>
            <Toggle
              checked={prefs.match_reminders}
              onChange={() => handleToggle("match_reminders")}
              disabled={saving}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <RefreshCw className="text-slate-400" size={18} />
              <div>
                <p className="text-white text-sm font-medium">Daily Rewards</p>
                <p className="text-slate-500 text-xs">Remind me to collect daily rewards</p>
              </div>
            </div>
            <Toggle
              checked={prefs.daily_reminders}
              onChange={() => handleToggle("daily_reminders")}
              disabled={saving}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Trophy className="text-slate-400" size={18} />
              <div>
                <p className="text-white text-sm font-medium">League Updates</p>
                <p className="text-slate-500 text-xs">Join requests, transfers, and more</p>
              </div>
            </div>
            <Toggle
              checked={prefs.league_notifications}
              onChange={() => handleToggle("league_notifications")}
              disabled={saving}
            />
          </div>
        </div>
      </div>

      {/* Rate & Review */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Star className="text-amber-400" size={18} />
            <div>
              <p className="text-white font-medium">Rate & Review</p>
              <p className="text-slate-500 text-xs">Love APL? Leave us a review!</p>
            </div>
          </div>
          <button
            onClick={handleRateAndReview}
            className="px-4 py-2 bg-amber-500/20 text-amber-400 text-sm font-medium rounded-lg hover:bg-amber-500/30 transition-all flex items-center gap-1.5"
            data-testid="rate-review-btn"
          >
            <Star size={14} className="fill-current" />
            Rate
          </button>
        </div>
      </div>

      {/* Replay Tutorial */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white font-medium">Replay Tutorial</p>
            <p className="text-slate-500 text-xs">See the app walkthrough again</p>
          </div>
          <button
            onClick={handleReplayTutorial}
            disabled={saving}
            className="px-4 py-2 bg-slate-700 text-white text-sm rounded-lg hover:bg-slate-600 transition-all disabled:opacity-50"
            data-testid="replay-tutorial-btn"
          >
            Replay
          </button>
        </div>
      </div>

      {/* App Info */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Info className="text-slate-400" size={18} />
          <h2 className="text-sm font-medium text-slate-400">App Info</h2>
        </div>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Version</span>
            <span className="text-slate-300" data-testid="app-version">{APP_VERSION}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Support</span>
            <a href="mailto:support@apnapremierleague.com" className="text-emerald-400 hover:underline">
              Contact Us
            </a>
          </div>
        </div>
      </div>

      {/* Delete Account */}
      <div className="bg-red-900/10 border border-red-900/30 rounded-xl p-4 mt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Trash2 className="text-red-400/60" size={18} />
            <div>
              <p className="text-red-400/80 font-medium">Delete Account</p>
              <p className="text-slate-500 text-xs">Permanently remove your account and all data</p>
            </div>
          </div>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-4 py-2 bg-red-500/10 text-red-400/80 text-sm font-medium rounded-lg hover:bg-red-500/20 transition-all"
            data-testid="settings-delete-account-btn"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Delete Account Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-red-500/30 rounded-2xl p-6 max-w-sm w-full" data-testid="settings-delete-dialog">
            <h2 className="text-xl font-bold text-white mb-3">Delete Account?</h2>
            <p className="text-slate-300 text-sm mb-4">
              Are you sure? This will permanently delete your account and all data. This action cannot be undone.
            </p>
            {deleteError && (
              <div className="bg-red-500/20 border border-red-500/50 text-red-400 rounded-lg p-3 mb-4 text-sm">
                {deleteError}
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteError(""); }}
                className="flex-1 py-3 bg-slate-700 text-white font-medium rounded-lg hover:bg-slate-600 transition-all"
                data-testid="settings-cancel-delete-btn"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setDeletingAccount(true);
                  setDeleteError("");
                  try {
                    const token = await user.getIdToken();
                    await axios.delete(`${API}/auth/delete-account`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    await logout();
                    navigate("/auth", { replace: true });
                  } catch (e) {
                    setDeleteError(e.response?.data?.detail || "Failed to delete account.");
                    setDeletingAccount(false);
                  }
                }}
                disabled={deletingAccount}
                className="flex-1 py-3 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="settings-confirm-delete-btn"
              >
                {deletingAccount ? <Loader2 className="animate-spin" size={16} /> : null}
                {deletingAccount ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
